// web/src/pages/admin/EmailTemplateInlineImagesPage.tsx
//
// Task B4 (E-6) — Inline-image library for email templates: upload/list/
// delete assets that the template editor can insert as CID images. Modeled
// on SpamAddressesPage.tsx.
import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { emailTemplateAssetsApi, type EmailTemplateAsset } from "@/lib/email-template-assets";
import { ApiError } from "@/lib/api";
import { ListPage } from "@/archetypes/ListPage";
import { DataGrid, type DataGridColumn } from "@/components/data-grid";
import { FileDropzone } from "@/components/file-dropzone";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useConfirm } from "@/hooks/use-confirm";
import { toast } from "@/hooks/use-toast";

function serverMessage(err: unknown, fallback: string): string {
  if (err instanceof ApiError) {
    const detail = (err.body as { detail?: string } | null)?.detail;
    if (detail) return detail;
  }
  return fallback;
}

/** Human-readable file size, e.g. 15000 -> "14.6 KB". */
function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function EmailTemplateInlineImagesPage() {
  const qc = useQueryClient();
  const confirm = useConfirm();
  const [uploading, setUploading] = useState(false);

  const { data: assets = [], isLoading } = useQuery({
    queryKey: ["email-template-assets"],
    queryFn: () => emailTemplateAssetsApi.list(),
  });

  const upload = useMutation({
    mutationFn: (file: File) => emailTemplateAssetsApi.upload(file),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["email-template-assets"] });
      toast.success("Image uploaded.");
    },
    onError: (err: unknown) => toast.error(serverMessage(err, "Upload failed.")),
  });

  const del = useMutation({
    mutationFn: (id: number) => emailTemplateAssetsApi.remove(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["email-template-assets"] });
      toast.success("Image deleted.");
    },
    onError: (err: unknown) => toast.error(serverMessage(err, "Delete failed.")),
  });

  async function handleFiles(files: File[]) {
    setUploading(true);
    try {
      for (const file of files) {
        // eslint-disable-next-line no-await-in-loop -- sequential upload keeps ordering + error attribution simple.
        await upload.mutateAsync(file);
      }
    } finally {
      setUploading(false);
    }
  }

  async function handleDelete(row: EmailTemplateAsset) {
    const ok = await confirm({
      title: "Delete image?",
      body: row.fileName,
      confirmLabel: "Delete",
      intent: "destructive",
    });
    if (ok) del.mutate(row.id);
  }

  const cols: DataGridColumn<EmailTemplateAsset>[] = [
    { id: "fileName", header: "File name", accessor: (r) => r.fileName },
    {
      id: "dimensions",
      header: "Dimensions",
      accessor: (r) => (r.width != null && r.height != null ? `${r.width}×${r.height}` : "—"),
    },
    { id: "size", header: "Size", accessor: (r) => formatSize(r.sizeBytes) },
    {
      id: "usedBy",
      header: "Used by",
      accessor: (r) => (
        <Badge variant={r.usedByTemplateIds.length > 0 ? "info" : "outline"}>
          {`Used by ${r.usedByTemplateIds.length}`}
        </Badge>
      ),
    },
    {
      id: "actions",
      header: "",
      accessor: (r) => (
        <div className="flex justify-end gap-1">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => void handleDelete(r)}
            disabled={del.isPending}
          >
            Delete
          </Button>
        </div>
      ),
      align: "right",
    },
  ];

  return (
    <ListPage backTo="/admin" title="Inline Images">
      <div className="space-y-4">
        <FileDropzone
          onFiles={(files) => void handleFiles(files)}
          accept="image/*"
          multiple
          disabled={uploading}
        />
        <DataGrid
          rows={assets}
          columns={cols}
          rowKey={(r) => r.id}
          totalRows={assets.length}
          pageIndex={0}
          pageSize={assets.length || 1}
          loading={isLoading}
          emptyHeadline="No inline images found."
          onPageChange={() => void 0}
        />
      </div>
    </ListPage>
  );
}
